import { Autocomplete, CircularProgress } from "@mui/material";
import axios from "axios";
import debounce from "lodash/debounce";
import React from "react";
import { CustomTextField } from "../formsComponents";

import { api } from "../../api/api";

interface IPlan {
  package_id?: number | null;
  package_name: string;
}

const initialialCountry: IPlan = {
  package_id: null,
  package_name: "",
};

const INITIAL_STATE: IPlan = initialialCountry;

interface IPlanAutoSearch {
  label?: string;
  onSelect: (value: IPlan) => void;
  initialValue?: string | null; // <-- Add initialValue prop
  disabled?: boolean;
  error?: string;
}

export const PlanAutoSearch: React.FC<IPlanAutoSearch> = (props) => {
  const { onSelect, initialValue, disabled, error } = props; // <-- Destructure initialValue
  const [options, setOptions] = React.useState<readonly IPlan[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [openData, setOpenData] = React.useState<boolean>(false);
  const [search, setSearchText] = React.useState("");
  const [initialPlan, setInitialPlan] = React.useState(initialValue || null); // <-- Set initialPlan to initialValue

  const fetchPlanSuggestions = async (searchValue: string) => {
    setLoading(true);
    try {
      const res = await api.get(`/get-packages?vehicle_type=Four_Wheeler`);
      const data: IPlan[] = res.data.data;
      setOptions(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const debounceFn = React.useCallback(debounce(fetchPlanSuggestions, 800), []);
  const getOptionLabel = (option: string | IPlan) => {
    if (typeof option === "string") return option;
    return option.package_name || "-1";
  };

  React.useEffect(() => {
    fetchPlanSuggestions("");
  }, []);

  React.useEffect(() => {
    if (openData && !initialPlan) {
      debounceFn(search);
    }
  }, [openData, search, initialPlan]);

  // Update initial plan when the initialValue changes
  React.useEffect(() => {
    if (initialValue) {
      setInitialPlan(initialValue); // <-- Set initial value from props
    }
  }, [initialValue]);

  return (
    <Autocomplete
      id="plan-auto-search"
      freeSolo
      options={options}
      value={initialPlan} // <-- Bind the value to initialPlan state
      getOptionLabel={getOptionLabel}
      noOptionsText="No Plans"
      onClose={() => setOpenData(false)}
      filterOptions={(x) => x}
      onFocus={() => setOpenData(true)}
      loading={loading}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        newValue: string | IPlan | null
      ) => {
        if (!newValue) {
          // Handle the case when the cross button is clicked
          setInitialPlan("");
          onSelect(INITIAL_STATE);
        } else if (typeof newValue === "string") {
          const newPlan: IPlan = {
            ...INITIAL_STATE,
            package_name: newValue,
          };
          setOptions([newPlan, ...options]);
          setInitialPlan(newPlan.package_name); // <-- Update the selected plan
          onSelect(newPlan);
        } else {
          setOptions([newValue, ...options]);
          setInitialPlan(newValue.package_name); // <-- Update the selected plan
          onSelect(newValue);
        }
      }}
      autoComplete
      includeInputInList
      onInputChange={(event, newInputValue) => {
        // When the input value is cleared (cross button), reset the state
        if (newInputValue === "" || newInputValue === null) {
          setSearchText("");
          setInitialPlan(""); // <-- Clear the Autocomplete value
          onSelect(INITIAL_STATE); // <-- Reset the selected plan
        } else if (event && event.type === "change") {
          setSearchText(newInputValue);
        }
      }}
      sx={{
        ".MuiOutlinedInput-root": {
          paddingTop: "2px",
          paddingBottom: "2px",
          fontSize: "0.8rem",
          color: "rgb(38, 38, 38)",
          width: "100%",
        },
      }}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          disabled={disabled}
          error={!!error}
          helperText={error}
          placeholder="Search Plan Name"
        />
      )}
      isOptionEqualToValue={(option, value) =>
        typeof option === "string"
          ? option === value
          : (option as IPlan).package_name === (value as IPlan).package_name
      }
    />
  );
};
